import { FormWizardPageElement } from "./models/form-wizard-template.model";
import { CreditCardDetails } from "./models/payment/credit-card-details.model";
import { InitialisePaymentPlanResult } from "./models/payment/initialise-payment-plan-result.model";
import { InitialisePaymentResult } from "./models/payment/initialise-payment-result.model";
import { ValueField } from "./models/settings/value-field.model";
import * as _ from 'lodash';

export class Common {
  static getFormattedCurrencyValue(value: number, addBracketsForNegative: boolean, removeMinusSign: boolean, alwaysAddBrackets: boolean = false, locale: string = 'en-AU', currency: string = 'AUD'): string {
      let addBrackets = false;
      let valueString = '';
      if (value !== 0) {
        if ((addBracketsForNegative && this.isNegativeNumber(value)) || alwaysAddBrackets) {
          addBrackets = true;
        }
  
        if (removeMinusSign && this.isNegativeNumber(value)) {
          value = this.removeMinusSign(value);
        }
  
        valueString = value.toLocaleString(locale, { style: 'currency', currency });
  
        if (addBrackets) {
          valueString = '(' + valueString + ')';
        }
      } else {
        valueString = value.toLocaleString(locale, { style: 'currency', currency });
      }
  
      return valueString;
  }

  static isNegativeNumber(value: number): boolean {
      if (value < 0) {
        return true;
      } else {
        return false;
      }
  }
  
  static removeMinusSign(value: number): number {
      return Math.abs(value);
  }

  static convertValueFieldToWizardElement(field: ValueField): FormWizardPageElement {
    const result = new FormWizardPageElement();

    if (!_.isNil(field)) {
      result.name = field.fldName;
      result.label = field.fldLabel;
      result.required = field.fldMandatory === 'True' ? true : false;
      result.class = field.fldWidth === 'Full' ? 'col-12' : 'col-12';

      switch (field.fldDataType) {
        case 'STRING':
          result.type = 'textInput';
          result.defaultValue = '';
          break;
        case 'POSTCODE':
          result.type = 'textInput';
          result.defaultValue = '';
          break;
        case 'DATE':
          result.type = 'dateInput';
          break;
      }
    }

    return result;
  }

  static getValidationEmailAddressRegex(): string {
    return '(?:[a-zA-Z0-9!#$%&\'*+/=?^_`{|}~-]+(?:\\.[a-zA-Z0-9!#$%&\'*+/=?^_`{|}~-]+)*|"(?:[\\x01-\\x08\\x0b\\x0c\\x0e-\\x1f\\x21\\x23-\\x5b\\x5d-\\x7f]|\\\\[\\x01-\\x09\\x0b\\x0c\\x0e-\\x7f])*")@(?:(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?|\\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-zA-Z0-9-]*[a-zA-Z0-9]:(?:[\\x01-\\x08\\x0b\\x0c\\x0e-\\x1f\\x21-\\x5a\\x53-\\x7f]|\\\\[\\x01-\\x09\\x0b\\x0c\\x0e-\\x7f])+)\\])';
  }

  static getValidationMobileNumberRegex(): string {
    return '\\+?(?<!\\d[ ]*)\\d(?:[ ]*\\d){7,}(?![ ]*\\d)';
  }

  static getValidationBankBSBRegex(): string {
    return '(?<!\\d[ -]*)\\d(?:[ -]*\\d){5}(?![ -]*\\d)';
  }

  static getValidationBankAccountNumberRegex(country: string): string {
    let result = '';

    switch (country) {
      case 'Australia':
        result = '(?<!\\d[ -]*)\\d(?:[ -]*\\d){5,9}(?![ -]*\\d)';
        break;
      case 'New Zealand':
        result = '(?<!\\d[ -]*)\\d(?:[ -]*\\d){14,15}(?![ -]*\\d)';
        break;
    }

    return result;
  }

  static stringHasValue(value: any): boolean {
    return !_.isNil(value) && value !== '';
  }

  static async createInitialisePaymentNABDirectPostForm(paymentInfo: InitialisePaymentResult, cardDetails: CreditCardDetails): Promise<HTMLFormElement> {
    const merchant = paymentInfo.nabMerchant ?? '';
    const type = '0';
    const referenceId = paymentInfo.nabReferenceId ?? '';
    const amount = paymentInfo.nabAmount ?? '';
    const timeStamp = paymentInfo.nabTimestamp ?? '';
    const fingerprint = paymentInfo.nabFingerPrint ?? '';
    const callbackUrl = paymentInfo.nabCallbackUrl ?? '';
    const resultUrl = paymentInfo.nabResultUrl ?? '';


    const nabForm = document.createElement('form') as HTMLFormElement;
    nabForm.method = 'POST';
    nabForm.action = paymentInfo.url ?? '';

    this.appendInputToForm('EPS_MERCHANT', merchant, 'hidden', nabForm);
    this.appendInputToForm('EPS_TXNTYPE', type, 'hidden', nabForm);
    this.appendInputToForm('EPS_REFERENCEID', referenceId, 'hidden', nabForm);
    this.appendInputToForm('EPS_AMOUNT', amount, 'hidden', nabForm);
    this.appendInputToForm('EPS_TIMESTAMP', timeStamp, 'hidden', nabForm);
    this.appendInputToForm('EPS_CARDNUMBER', cardDetails.cardNumber, 'hidden', nabForm);
    this.appendInputToForm('EPS_EXPIRYMONTH', cardDetails.expiryMonth, 'hidden', nabForm);
    this.appendInputToForm('EPS_EXPIRYYEAR', cardDetails.expiryYear, 'hidden', nabForm);
    this.appendInputToForm('EPS_CCV', cardDetails.cvvNumber, 'hidden', nabForm);
    this.appendInputToForm('EPS_FINGERPRINT', fingerprint, 'hidden', nabForm);
    this.appendInputToForm('EPS_CALLBACKURL', callbackUrl, 'hidden', nabForm);
    this.appendInputToForm('EPS_REDIRECT', 'true', 'hidden', nabForm);
    this.appendInputToForm('EPS_RESULTURL', resultUrl, 'hidden', nabForm);

    return nabForm;
  }

  static async createInitialisePaymentPlanNABDirectPostForm(paymentInfo: InitialisePaymentPlanResult, cardDetails: CreditCardDetails): Promise<HTMLFormElement> {
    const merchant = paymentInfo.nabMerchant ?? '';
    const type = '8';
    const referenceId = paymentInfo.nabReferenceId ?? '';
    const timeStamp = paymentInfo.nabTimestamp ?? '';
    const fingerprint = paymentInfo.nabFingerPrint ?? '';
    const callbackUrl = paymentInfo.nabCallbackUrl ?? '';
    const resultUrl = paymentInfo.nabResultUrl ?? '';

    const nabForm = document.createElement('form') as HTMLFormElement;
    nabForm.method = 'POST';
    nabForm.action = paymentInfo.url ?? '';

    this.appendInputToForm('EPS_MERCHANT', merchant, 'hidden', nabForm);
    this.appendInputToForm('EPS_TXNTYPE', type, 'hidden', nabForm);
    this.appendInputToForm('EPS_REFERENCEID', referenceId, 'hidden', nabForm);
    this.appendInputToForm('EPS_TIMESTAMP', timeStamp, 'hidden', nabForm);
    this.appendInputToForm('EPS_CARDNUMBER', cardDetails.cardNumber, 'hidden', nabForm);
    this.appendInputToForm('EPS_EXPIRYMONTH', cardDetails.expiryMonth, 'hidden', nabForm);
    this.appendInputToForm('EPS_EXPIRYYEAR', cardDetails.expiryYear, 'hidden', nabForm);
    this.appendInputToForm('EPS_CCV', cardDetails.cvvNumber, 'hidden', nabForm);
    this.appendInputToForm('EPS_STORE', 'true', 'hidden', nabForm);
    this.appendInputToForm('EPS_STORETYPE', 'TOKEN', 'hidden', nabForm);
    this.appendInputToForm('EPS_FINGERPRINT', fingerprint, 'hidden', nabForm);
    this.appendInputToForm('EPS_CALLBACKURL', callbackUrl, 'hidden', nabForm);
    this.appendInputToForm('EPS_REDIRECT', 'true', 'hidden', nabForm);
    this.appendInputToForm('EPS_RESULTURL', resultUrl, 'hidden', nabForm);

    return nabForm;
  }

  static async createInitialisePaymentMWForm(paymentInfo: InitialisePaymentResult): Promise<HTMLFormElement> {
    const form = document.createElement('form') as HTMLFormElement;
    form.method = 'POST';
    form.action = paymentInfo.url ?? '';

    this.appendInputToForm('method', 'processCard', 'hidden', form);
    this.appendInputToForm('merchantUUID', paymentInfo.mwMerchant ?? '', 'hidden', form);
    this.appendInputToForm('apiKey', paymentInfo.mwApiKey ?? '', 'hidden', form);
    this.appendInputToForm('transactionAmount', paymentInfo.mwAmount ?? '', 'hidden', form);
    this.appendInputToForm('transactionCurrency', paymentInfo.mwCurrency ?? '', 'hidden', form);
    this.appendInputToForm('transactionProduct', paymentInfo.mwProduct ?? '', 'hidden', form);
    this.appendInputToForm('returnURL', paymentInfo.mwReturnUrl ?? '', 'hidden', form);
    this.appendInputToForm('notifyURL', paymentInfo.mwNotifyUrl ?? '', 'hidden', form);
    this.appendInputToForm('urlHash', paymentInfo.mwUrlHash ?? '', 'hidden', form);
    this.appendInputToForm('hashSalt', paymentInfo.mWhashSalt ?? '', 'hidden', form);
    this.appendInputToForm('hash', paymentInfo.mWhash ?? '', 'hidden', form);
    this.appendInputToForm('transactionReferenceID', paymentInfo.mwReferenceId ?? '', 'hidden', form);
    this.appendInputToForm('returnOnApproved', '1', 'hidden', form);
    this.appendInputToForm('hideReference', '1', 'hidden', form);
    this.appendInputToForm('surcharge', '0', 'hidden', form);

    return form;
  }

  static appendInputToForm(name: string, value: string, type: string, form: HTMLFormElement): void {
    const input = document.createElement('INPUT') as HTMLInputElement;         
    input.name = name;
    input.value = value;
    input.type = type;
    form.appendChild(input);
  }
}
